import { useState, useCallback, useMemo, memo } from 'react';
import {
    Box,
    Flex,
    VStack,
    // HStack,
    Text,
    IconButton,
    Tooltip,
    Drawer,
    DrawerOverlay,
    DrawerContent,
    DrawerBody,
    useDisclosure,
    Image,
    Badge,
} from '@chakra-ui/react';
import { NavLink, useMatch, useResolvedPath, Link as RouterLink } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { clearFrom } from '../../../redux/actions/profileActions';
import logo from '../../../assets/bigLogo.svg';

/* ─────────────────────────────────────────────────────────────
   BRAND TOKENS — mirrors LargeDashboard / DashHead
   ───────────────────────────────────────────────────────────── */

const NAVY        = '#1B3B5F';
// const NAVY_DEEP   = '#0F2940';
const CYAN        = '#DCF8F9';
const INK         = '#1A2740';
const MUTED       = '#5A6B7E';
const FAINT       = '#8896AE';
const SURFACE     = '#FFFFFF';
const SURFACE_ALT = '#F7F9FB';
const BORDER      = '#E8EDF5';

const FONT = "'Bricolage Grotesque', sans-serif";

const SIDEBAR_WIDTH_EXPANDED  = '240px';
const SIDEBAR_WIDTH_COLLAPSED = '72px';
const SIDEBAR_WIDTH_MOBILE    = '280px';

const STORAGE_KEY = 'obana.sidebar.collapsed';

/* ─────────────────────────────────────────────────────────────
   ICONS — inline SVG, consistent stroke weight
   ───────────────────────────────────────────────────────────── */

const I = {
    Dashboard: (p) => (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" {...p}>
            <rect x="3" y="3" width="7" height="9" rx="2" stroke="currentColor" strokeWidth="1.8" />
            <rect x="14" y="3" width="7" height="5" rx="2" stroke="currentColor" strokeWidth="1.8" />
            <rect x="14" y="12" width="7" height="9" rx="2" stroke="currentColor" strokeWidth="1.8" />
            <rect x="3" y="16" width="7" height="5" rx="2" stroke="currentColor" strokeWidth="1.8" />
        </svg>
    ),
    Products: (p) => (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" {...p}>
            <path d="M3 7l9-4 9 4-9 4-9-4z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
            <path d="M3 12l9 4 9-4 M3 17l9 4 9-4" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
        </svg>
    ),
    Wishlist: (p) => (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" {...p}>
            <path
                d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinejoin="round"
            />
        </svg>
    ),
    Orders: (p) => (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" {...p}>
            <rect x="4" y="4" width="16" height="16" rx="2" stroke="currentColor" strokeWidth="1.8" />
            <path d="M8 10h8 M8 14h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
    ),
    CustomSourcing: (p) => (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" {...p}>
            <rect x="4" y="4" width="16" height="16" rx="2" stroke="currentColor" strokeWidth="1.8" />
            <path d="M8 10h8 M8 14h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
    ),
    task: (p) => (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" {...p}>
            <rect x="4" y="4" width="16" height="16" rx="2" stroke="currentColor" strokeWidth="1.8" />
            <path d="M8 10h8 M8 14h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
    ),
    Shipments: (p) => (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" {...p}>
            <path d="M2 8h13v9H2zM15 12h5l2 3v2h-7z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
            <circle cx="6.5" cy="18.5" r="1.5" stroke="currentColor" strokeWidth="1.8" />
            <circle cx="17.5" cy="18.5" r="1.5" stroke="currentColor" strokeWidth="1.8" />
        </svg>
    ),
    Transactions: (p) => (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" {...p}>
            <rect x="2" y="6" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="1.8" />
            <path d="M2 10h20 M6 16h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
    ),
    CRM: (p) => (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" {...p}>
            <circle cx="9" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.8" />
            <path d="M2 21c0-3.87 3.13-7 7-7s7 3.13 7 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            <circle cx="17" cy="6" r="2.5" stroke="currentColor" strokeWidth="1.8" />
            <path d="M17 11c2.76 0 5 1.94 5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
    ),
    Store: (p) => (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" {...p}>
            <path d="M3 9l1.5-5h15L21 9 M3 9v11h18V9 M3 9h18 M8 14h8" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
        </svg>
    ),
    Settings: (p) => (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" {...p}>
            <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8" />
            <path
                d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinejoin="round"
            />
        </svg>
    ),
    ChevronLeft: (p) => (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" {...p}>
            <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    Menu: (p) => (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" {...p}>
            <path d="M4 6h16M4 12h16M4 18h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
    ),
    Close: (p) => (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" {...p}>
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
    ),
};

/* ─────────────────────────────────────────────────────────────
   NAV CONFIG
   ───────────────────────────────────────────────────────────── */

const NAV_PRIMARY = [
    { label: 'Dashboard',     icon: I.Dashboard,    path: '/dashboard',           end: true },
    { label: 'Products',      icon: I.Products,     path: '/dashboard/products' },
    { label: 'Wishlist',      icon: I.Wishlist,     path: '/dashboard/import' }, // TODO BACKEND: rename to /wishlist for clarity
    { label: 'Orders',        icon: I.Orders,       path: '/dashboard/orders' },
    { label: 'Shipments',     icon: I.Shipments,    path: '/dashboard/shipments' },
    { label: 'Transactions',  icon: I.Transactions, path: '/dashboard/wallet' },  // TODO BACKEND: rename to /transactions for clarity
    { label: 'CRM',           icon: I.CRM,          path: '/dashboard/crm' },
    { label: 'Custom Sourcing',           icon: I.CustomSourcing, type: 'external', href: "http://obana.africa/custom-sourcing" },
    { label: 'Your task',           icon: I.task, type: 'external', href: "http://obana.africa/task" },
];

const NAV_SECONDARY = [
    // 'Create Store' is conditional — handled at render time
    { label: 'Settings',  icon: I.Settings,  path: '/dashboard/settings', onActivate: 'clearForm' },
];

/* ─────────────────────────────────────────────────────────────
   NAV ITEM
   ───────────────────────────────────────────────────────────── */

// const NavItem = memo(function NavItem({
//     label,
//     icon: Icon,
//     path,
//     href,
//     type,
//     end,
//     badge,
//     disabled,
//     isCollapsed,
//     onClick,
// }) {
//     const isExternal = type === 'external' || Boolean(href);
//     // Active-state matching: 'end' means strict match, otherwise startsWith
//     const resolved   = useResolvedPath(path);
//     const matchExact = useMatch({ path: resolved.pathname, end: true });
//     const matchSubs  = useMatch({ path: resolved.pathname, end: false });
//     const isActive   = end ? !!matchExact : !!matchSubs;

//     const content = (
//         <Flex
//             as={disabled ? 'div' : NavLink}
//             to={disabled ? undefined : path}
//             onClick={onClick}
//             align="center"
//             gap="12px"
//             px={isCollapsed ? '0' : '12px'}
//             py="10px"
//             mx={isCollapsed ? '12px' : '8px'}
//             borderRadius="10px"
//             cursor={disabled ? 'not-allowed' : 'pointer'}
//             color={isActive ? NAVY : MUTED}
//             bg={isActive ? CYAN : 'transparent'}
//             fontWeight={isActive ? '700' : '600'}
//             fontSize="13px"
//             fontFamily={FONT}
//             opacity={disabled ? 0.5 : 1}
//             justifyContent={isCollapsed ? 'center' : 'flex-start'}
//             transition="background 0.15s ease, color 0.15s ease"
//             aria-current={isActive ? 'page' : undefined}
//             aria-disabled={disabled || undefined}
//             _hover={
//                 disabled
//                     ? undefined
//                     : isActive
//                     ? { bg: CYAN }
//                     : { bg: SURFACE_ALT, color: NAVY }
//             }
//             _focusVisible={{ outline: `2px solid ${NAVY}`, outlineOffset: '2px' }}
//             sx={{ textDecoration: 'none' }}
//         >
//             <Box flexShrink={0} display="flex" alignItems="center" justifyContent="center">
//                 <Icon />
//             </Box>
//             {!isCollapsed && (
//                 <>
//                     <Text flex="1" noOfLines={1}>
//                         {label}
//                     </Text>
//                     {badge && (
//                         <Badge
//                             bg={NAVY}
//                             color="white"
//                             fontSize="9px"
//                             fontWeight="700"
//                             px="6px"
//                             py="2px"
//                             borderRadius="4px"
//                             letterSpacing="0.04em"
//                         >
//                             {badge}
//                         </Badge>
//                     )}
//                 </>
//             )}
//         </Flex>
//     );

//     if (isCollapsed) {
//         return (
//             <Tooltip label={label} placement="right" hasArrow openDelay={150} bg={INK} color="white" fontSize="12px">
//                 {content}
//             </Tooltip>
//         );
//     }
//     return content;
// });

const NavItem = memo(function NavItem({
    label,
    icon: Icon,
    path,
    href,
    type,
    end,
    badge,
    disabled,
    isCollapsed,
    onClick,
}) {
    const isExternal = type === 'external' || Boolean(href);

    // Always call hooks – use a safe fallback path for external items
    const safePath = isExternal ? '#' : path;
    const resolved = useResolvedPath(safePath);
    const matchExact = useMatch({ path: resolved.pathname, end: true });
    const matchSubs  = useMatch({ path: resolved.pathname, end: false });

    // Only treat as active for internal items
    const isActive = !isExternal && (end ? !!matchExact : !!matchSubs);

    const commonProps = {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        px: isCollapsed ? '0' : '12px',
        py: '10px',
        mx: isCollapsed ? '12px' : '8px',
        borderRadius: '10px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        color: isActive ? NAVY : MUTED,
        bg: isActive ? CYAN : 'transparent',
        fontWeight: isActive ? '700' : '600',
        fontSize: '13px',
        fontFamily: FONT,
        opacity: disabled ? 0.5 : 1,
        justifyContent: isCollapsed ? 'center' : 'flex-start',
        transition: 'background 0.15s ease, color 0.15s ease',
        textDecoration: 'none',
    };

    const content = isExternal ? (
        <Flex
            as="a"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onClick}
            aria-label={`${label} (opens in new tab)`}
            _hover={disabled ? undefined : { bg: SURFACE_ALT, color: NAVY }}
            _focusVisible={{ outline: `2px solid ${NAVY}`, outlineOffset: '2px' }}
            {...commonProps}
        >
            <Box flexShrink={0} display="flex" alignItems="center" justifyContent="center">
                <Icon />
            </Box>
            {!isCollapsed && <Text flex="1" noOfLines={1}>{label}</Text>}
        </Flex>
    ) : (
        <Flex
            as={disabled ? 'div' : NavLink}
            to={disabled ? undefined : path}
            onClick={onClick}
            aria-current={isActive ? 'page' : undefined}
            aria-disabled={disabled || undefined}
            _hover={disabled ? undefined : isActive ? { bg: CYAN } : { bg: SURFACE_ALT, color: NAVY }}
            _focusVisible={{ outline: `2px solid ${NAVY}`, outlineOffset: '2px' }}
            {...commonProps}
        >
            <Box flexShrink={0} display="flex" alignItems="center" justifyContent="center">
                <Icon />
            </Box>
            {!isCollapsed && (
                <>
                    <Text flex="1" noOfLines={1}>{label}</Text>
                    {badge && (
                        <Badge
                            bg={NAVY}
                            color="white"
                            fontSize="9px"
                            fontWeight="700"
                            px="6px"
                            py="2px"
                            borderRadius="4px"
                            letterSpacing="0.04em"
                        >
                            {badge}
                        </Badge>
                    )}
                </>
            )}
        </Flex>
    );

    if (isCollapsed) {
        return (
            <Tooltip label={label} placement="right" hasArrow openDelay={150} bg={INK} color="white" fontSize="12px">
                {content}
            </Tooltip>
        );
    }
    return content;
});

/* ─────────────────────────────────────────────────────────────
   SIDEBAR CONTENT (shared between desktop sticky + mobile drawer)
   ───────────────────────────────────────────────────────────── */

const SidebarContent = memo(function SidebarContent({
    isCollapsed,
    onToggleCollapse,
    onCloseMobile,
    hasStore,
    onSettingsClick,
    isMobile,
}) {
    const closeMobileAndContinue = useCallback(() => {
        if (isMobile && onCloseMobile) onCloseMobile();
    }, [isMobile, onCloseMobile]);

    const storeItem = useMemo(
        () => ({
            label:    'Create Store',
            icon:     I.Store,
            path:     hasStore ? '/dashboard/store' : '/dashboard/coming-soon',
            badge:    hasStore ? null : 'SOON',
        }),
        [hasStore]
    );

    return (
        <Flex
            as="aside"
            direction="column"
            h="100vh"
            w="full"
            bg={SURFACE}
            borderRight={isMobile ? 'none' : '1px solid'}
            borderColor={BORDER}
            fontFamily={FONT}
        >
            {/* Header */}
            <Flex
                align="center"
                justify={isCollapsed ? 'center' : 'space-between'}
                p={isCollapsed ? '20px 0' : '20px 20px'}
                borderBottom="1px solid"
                borderColor={BORDER}
                h="72px"
                flexShrink={0}
            >
                <RouterLink to="/dashboard" onClick={closeMobileAndContinue}>
                    {isCollapsed ? (
                        <Box
                            w="36px"
                            h="36px"
                            borderRadius="10px"
                            bg={NAVY}
                            color="white"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            fontFamily={FONT}
                            fontWeight="800"
                            fontSize="16px"
                        >
                            O
                        </Box>
                    ) : (
                        <Image src={logo} alt="Obana" h="28px" style={{ width: 'auto', height: 'auto', maxHeight: '28px' }} />
                    )}
                </RouterLink>

                {!isCollapsed && !isMobile && (
                    <IconButton
                        aria-label="Collapse sidebar"
                        icon={<I.ChevronLeft />}
                        onClick={onToggleCollapse}
                        variant="ghost"
                        size="sm"
                        color={MUTED}
                        _hover={{ bg: SURFACE_ALT, color: NAVY }}
                        borderRadius="8px"
                    />
                )}

                {isMobile && (
                    <IconButton
                        aria-label="Close menu"
                        icon={<I.Close />}
                        onClick={onCloseMobile}
                        variant="ghost"
                        size="sm"
                        color={MUTED}
                        _hover={{ bg: SURFACE_ALT, color: NAVY }}
                        borderRadius="8px"
                    />
                )}
            </Flex>

            {/* Expand toggle (only when collapsed on desktop) */}
            {isCollapsed && !isMobile && (
                <Flex justify="center" p="12px 0" borderBottom="1px solid" borderColor={BORDER}>
                    <IconButton
                        aria-label="Expand sidebar"
                        icon={
                            <Box transform="rotate(180deg)">
                                <I.ChevronLeft />
                            </Box>
                        }
                        onClick={onToggleCollapse}
                        variant="ghost"
                        size="sm"
                        color={MUTED}
                        _hover={{ bg: SURFACE_ALT, color: NAVY }}
                        borderRadius="8px"
                    />
                </Flex>
            )}

            {/* Primary nav — scrollable if overflows */}
            <Box flex="1" overflowY="auto" overflowX="hidden" py="12px">
                {/* {!isCollapsed && (
                    <Text
                        px="20px"
                        mb="8px"
                        fontSize="10px"
                        fontWeight="700"
                        color={FAINT}
                        letterSpacing="0.12em"
                        textTransform="uppercase"
                    >
                        Main
                    </Text>
                )} */}
                <VStack as="nav" align="stretch" spacing="2px" aria-label="Main navigation">
                    {NAV_PRIMARY.map((item) => (
                        <NavItem
                            key={item.path || item.href || item.label}
                            {...item}
                            isCollapsed={isCollapsed}
                            onClick={closeMobileAndContinue}
                        />
                    ))}
                </VStack>
            </Box>

            {/* Bottom nav */}
            <Box borderTop="1px solid" borderColor={BORDER} py="12px" flexShrink={0}>
                {!isCollapsed && (
                    <Text
                        px="20px"
                        mb="8px"
                        fontSize="10px"
                        fontWeight="700"
                        color={FAINT}
                        letterSpacing="0.12em"
                        textTransform="uppercase"
                    >
                        Workspace
                    </Text>
                )}
                <VStack as="nav" align="stretch" spacing="2px" aria-label="Secondary navigation">
                    <NavItem
                        {...storeItem}
                        isCollapsed={isCollapsed}
                        onClick={closeMobileAndContinue}
                    />
                    {NAV_SECONDARY.map((item) => (
                        <NavItem
                            key={item.path || item.href || item.label}
                            {...item}
                            isCollapsed={isCollapsed}
                            onClick={() => {
                                if (item.onActivate === 'clearForm') onSettingsClick();
                                closeMobileAndContinue();
                            }}
                        />
                    ))}
                </VStack>
            </Box>
        </Flex>
    );
});

/* ─────────────────────────────────────────────────────────────
   MAIN
   ───────────────────────────────────────────────────────────── */

const Navigation = () => {
    const dispatch = useDispatch();
    const { store } = useSelector((state) => state.storeReducer);
    const hasStore = !!store;

    const mobileDrawer = useDisclosure();

    /* Persisted collapse state — defaults to EXPANDED.
       Only user clicking the chevron toggles this. */
    const [isCollapsed, setIsCollapsed] = useState(() => {
        if (typeof window === 'undefined') return false;
        try {
            return window.localStorage.getItem(STORAGE_KEY) === 'true';
        } catch {
            return false;
        }
    });

    const toggleCollapse = useCallback(() => {
        setIsCollapsed((prev) => {
            const next = !prev;
            try {
                window.localStorage.setItem(STORAGE_KEY, String(next));
            } catch {
                /* localStorage may be blocked (private mode) */
            }
            return next;
        });
    }, []);

    const handleSettingsClick = useCallback(() => {
        dispatch(clearFrom());
    }, [dispatch]);

    return (
        <>
            {/* Mobile hamburger trigger — visible only on <lg */}
            <IconButton
                aria-label="Open menu"
                icon={<I.Menu />}
                onClick={mobileDrawer.onOpen}
                position="fixed"
                top="14px"
                left="14px"
                zIndex="docked"
                bg={SURFACE}
                border="1px solid"
                borderColor={BORDER}
                color={INK}
                size="md"
                borderRadius="10px"
                boxShadow="0 1px 3px rgba(27,59,95,0.06)"
                _hover={{ bg: SURFACE_ALT }}
                display={{ base: 'inline-flex', lg: 'none' }}
            />

            {/* Desktop sidebar — visible only on lg+, always rendered */}
            <Box
                as="nav"
                w={isCollapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED}
                flexShrink={0}
                position="sticky"
                top="0"
                h="100vh"
                transition="width 0.25s cubic-bezier(.2,.7,.3,1)"
                zIndex="docked"
                display={{ base: 'none', lg: 'block' }}
            >
                <SidebarContent
                    isCollapsed={isCollapsed}
                    onToggleCollapse={toggleCollapse}
                    hasStore={hasStore}
                    onSettingsClick={handleSettingsClick}
                    isMobile={false}
                />
            </Box>

            {/* Mobile drawer */}
            <Drawer
                isOpen={mobileDrawer.isOpen}
                placement="left"
                onClose={mobileDrawer.onClose}
                size="xs"
            >
                <DrawerOverlay bg="blackAlpha.500" />
                <DrawerContent maxW={SIDEBAR_WIDTH_MOBILE}>
                    <DrawerBody p="0" overflow="auto">
                        <SidebarContent
                            isCollapsed={false}
                            onCloseMobile={mobileDrawer.onClose}
                            hasStore={hasStore}
                            onSettingsClick={handleSettingsClick}
                            isMobile={true}
                        />
                    </DrawerBody>
                </DrawerContent>
            </Drawer>
        </>
    );
};

export default Navigation;